import { SelectOption } from "@/components/FormInputs/types";
import { format } from "date-fns";
import {
  StudentTypeEnum,
  YesNoEnum,
  JoinTypeEnum,
  GoingStatusEnum,
  CountryClassificationEnum,
  GenderEnum,
  UnitedStatesStateEnum,
  PredictionIndexEnum,
  EntryTermEnum,
  FafsaCompletionStatusEnum,
  RisePredictiveIndexEnum,
  RisePredictiveModelEnum,
  OrganizationPartnershipLevelEnum,
  PartnerFeatureEnum,
  EngagementPercentileEnum,
  PredictiveScoreTrendEnum,
} from "@/__generated__/types";
import { getGroupNames } from "@/util/funnelGroups";
import { getJoinStateGroupNames } from "@/util/joinStateGroups";
import { z } from "zod/v4";

export const DateRange = z
  .object({
    to: z.optional(z.coerce.date()),
    from: z.optional(z.coerce.date()),
  })
  .strict();

export const FilterInputData = z.object({
  goingElsewhere: z.nullish(z.enum(YesNoEnum)).default(null),
  moreActiveElsewhere: z.nullish(z.enum(YesNoEnum)).default(null),
  assignedCounselors: z.array(z.string()).default([]),
  attendedALiveEvent: z.nullish(z.enum(YesNoEnum)).default(null),
  campusVisit: z.nullish(z.enum(YesNoEnum)).default(null),
  ceebCode: z.array(z.string()).default([]),
  cohortYear: z.array(z.string()).default([]),
  commitIndex: z.array(z.enum(PredictionIndexEnum)).default([]),
  fafsaCompletionStatus: z.array(z.enum(FafsaCompletionStatusEnum)).default([]),
  country: z.nullish(z.enum(CountryClassificationEnum)).default(null),
  deposited: z.nullish(z.enum(YesNoEnum)).default(null),
  directAdmission: z.nullish(z.enum(YesNoEnum)).default(null),
  entryTerm: z.array(z.enum(EntryTermEnum)).default([]),
  firstGeneration: z.nullish(z.enum(YesNoEnum)).default(null),
  funnelStates: z.array(z.enum(getGroupNames("SIDEBAR"))).default([]),
  gender: z.array(z.enum(GenderEnum)).default([]),
  goingStatus: z.array(z.enum(GoingStatusEnum)).default([]),
  interests: z.array(z.string()).default([]),
  joinDate: z.optional(DateRange).default({}),
  joinState: z.array(z.enum(getJoinStateGroupNames())).default([]),
  joinType: z.array(z.enum(JoinTypeEnum)).default([]),
  leadSource: z.array(z.string()).default([]),
  majors: z.array(z.string()).default([]),
  state: z.array(z.enum(UnitedStatesStateEnum)).default([]),
  studentAthlete: z.nullish(z.enum(YesNoEnum)).default(null),
  studentType: z.nullish(z.enum(StudentTypeEnum)).default(null),
  zipCode: z.array(z.string()).default([]),
  inCommunity: z.nullish(z.enum(YesNoEnum)).default(null),
  applyIndex: z.array(z.enum(RisePredictiveIndexEnum)).default([]),
  riseCommitIndex: z.array(z.enum(RisePredictiveIndexEnum)).default([]),
  meltIndex: z.array(z.enum(RisePredictiveIndexEnum)).default([]),
  yieldIndex: z.array(z.enum(RisePredictiveIndexEnum)).default([]),
  upTrendingScores: z.array(z.enum(RisePredictiveModelEnum)).default([]),
  downTrendingScores: z.array(z.enum(RisePredictiveModelEnum)).default([]),
  engagementPercentile: z
    .nullish(z.enum(EngagementPercentileEnum))
    .default(null),
  commitDate: z.optional(DateRange).default({}),
  commitScoreTrend: z.nullish(z.enum(PredictiveScoreTrendEnum)).default(null),
  hasProfilePhoto: z.nullish(z.enum(YesNoEnum)).default(null),
});

// Hide from active filter count
export const HIDDEN_FILTERS: FilterName[] = ["inCommunity"];

export type FilterInputData = z.infer<typeof FilterInputData>;

export type FilterName = keyof FilterInputData;

export type MultiSelect = string[];

export type SingleSelect = string | null;

export type FilterDataType = MultiSelect | DateRange | SingleSelect;

export type InputType =
  | "Checkboxes"
  | "Radio"
  | "Date"
  | "DateRange"
  | "Boolean"
  | "DropdownMultiSelect"
  | "TokenizedTextInput";

export interface FilterGroup {
  title: string;
  filters: FilterInput[];
}

type DisplayType = "RISE" | undefined;

export type IsFilterDisabled = ({
  partnershipLevel,
  featureFlags,
}: {
  partnershipLevel?: OrganizationPartnershipLevelEnum;
  featureFlags?: PartnerFeatureEnum[];
}) => boolean;

export interface FilterInput {
  label: string;
  name: FilterName;
  type: InputType;
  placeholder?: string;
  disableSearch?: boolean;
  options?: SelectOption[];
  tokenIsValid?: (val: string) => boolean;
  filterDisplayType?: DisplayType;
  isDisabled?: IsFilterDisabled;
}

// Utility functions for types:
export const isDateRange = (value: unknown): value is DateRange =>
  DateRange.safeParse(value).success;

export function isSingleSelect(value: unknown): value is SingleSelect {
  return typeof value === "string" || value === null;
}

export function isMultiSelect(value: unknown): value is MultiSelect {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}
export function multiSelectAreEqual(a: MultiSelect, b: MultiSelect) {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return [...a].every((value) => setB.has(value));
}

export function dateRangesAreEqual(a: DateRange, b: DateRange) {
  const f = "yyyy-MM-dd";
  if ((a.to && format(a.to, f)) !== (b.to && format(b.to, f))) return false;
  if ((a.from && format(a.from, f)) !== (b.from && format(b.from, f)))
    return false;
  return true;
}

export type DateRange = z.infer<typeof DateRange>;
